import React, { useEffect, useState } from 'react';
import { Formik, ErrorMessage } from 'formik';

import LoadMask from 'components/LoadMask';
import BasicButtonGroup from 'components/BasicButtonGroup';
import BasicButton from 'components/BasicButton';
import BasicSubmitButton from 'components/BasicSubmitButton';
import BasicInput from 'components/BasicInput';
import BasicLabel from 'components/BasicLabel';
import BasicSelect from 'components/BasicSelect';
import TextEditor from 'components/TextEditor';
import CheckboxOrRadioGroup from 'components/CheckboxOrRadioGroup';
import HorizontalLoader from 'components/HorizontalLoader';
import LazyLoadSelect from 'components/LazyLoadSelect';

import { CSSTransition } from 'react-transition-group';
import { ANIMATION_TIMEOUT_APPEAR } from 'constants/animation.js';
import { useApi } from 'hooks/useApi';
import { ENDPOINT } from 'constants/endpoints';
import { Normalizer } from 'scenes/Form/components/FirmOverviewEditForm/normalizer';
import { initialData } from 'scenes/Form/components/FirmOverviewEditForm/initialData';
import { FirmOverviewEditFormValidationSchema } from 'scenes/Form/components/FirmOverviewEditForm/validate';

import './styles.css';

const FirmOverviewEditForm = props => {
  const {
    params: { id },
    actions: { proceed, dismiss, handleError },
  } = props;

  const api = useApi();
  const [formData, setFormData] = useState(initialData);

  useEffect(() => {
    api.get(
      ENDPOINT.FORM.GET.FIRM_OVERVIEW,
      { id },
      {
        handleError: response => handleError(response),
        handleSuccess: response => setFormData(Normalizer.fetch(response)),
      }
    );
  }, []);

  const getFirmOptions = (query, offset) =>
    new Promise((resolve, reject) => {
      api.get(
        ENDPOINT.OTHER.FIRM_OVERVIEW_LOCATION_OPTIONS,
        { query, offset },
        {
          handleError: response => reject(response),
          handleSuccess: data => resolve(data),
        }
      );
    });

  const onSubmit = (values, { setSubmitting, setErrors }) => {
    api.post(
      ENDPOINT.FORM.SAVE.FIRM_OVERVIEW,
      { ...Normalizer.send(values), id },
      {
        handleError: response => {
          setSubmitting(false);
          handleError(response, setErrors);
        },
        handleSuccess: response => proceed(response),
      }
    );
  };

  return formData.init ? (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <div className="form__inner">
        <Formik
          initialValues={formData.fields}
          onSubmit={onSubmit}
          validationSchema={FirmOverviewEditFormValidationSchema}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
          }) => (
            <form onSubmit={handleSubmit} className="form__content">
              {isSubmitting && <LoadMask />}
              <div className="form__group form__group--compact">
                <BasicInput
                  label="Website"
                  required={false}
                  name="Site"
                  inputType="text"
                  content={values.Site || ''}
                  placeholder="Website..."
                  controlFunc={handleChange}
                  isValid={(!errors.Site && touched.Site) || !touched.Site}
                  touchFunc={handleBlur}
                  labelStyles={{ fontWeight: 'bold' }}
                />

                <ErrorMessage
                  name="Site"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div className="form__group form__group--compact">
                <LazyLoadSelect
                  name="OfficeLocationID"
                  isMulti={false}
                  value={values.OfficeLocationID}
                  isValid={
                    (!errors.OfficeLocationID && !!touched.OfficeLocationID) ||
                    !touched.OfficeLocationID
                  }
                  label="Headquarters"
                  labelStyles={{
                    fontWeight: 'bold',
                  }}
                  required={false}
                  placeholder="Start Typing"
                  getOption={getFirmOptions}
                  onChange={setFieldValue}
                  onBlur={setFieldTouched}
                  isClearable={true}
                />

                <ErrorMessage
                  name="OfficeLocationID"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div className="form__group form__group--compact">
                <BasicLabel
                  name="FirmSizeID"
                  required={false}
                  text="Company Size"
                  labelStyles={{
                    fontWeight: 'bold',
                  }}
                />
                <BasicSelect
                  name="FirmSizeID"
                  value={values.FirmSizeID}
                  isValid={
                    (!errors.FirmSizeID && !!touched.FirmSizeID) ||
                    !touched.FirmSizeID
                  }
                  options={formData.options.FirmSizeID}
                  required={true}
                  isSearchable={false}
                  isClearable={true}
                  placeholder="Select from list"
                  onChange={setFieldValue}
                  onBlur={setFieldTouched}
                />

                <ErrorMessage
                  name="FirmSizeID"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div
                className="form__group form__group--compact"
                style={{ marginBottom: '15px' }}
              >
                <BasicLabel name="Verified" required={true} text="Verified" />
                <CheckboxOrRadioGroup
                  name="Verified"
                  type="radio"
                  options={['Yes', 'No']}
                  selectedOptions={values.Verified || ''}
                  directionRow={true}
                  controlFunc={handleChange}
                />
                <ErrorMessage
                  name="Verified"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div className="form__group form__group--compact">
                <BasicLabel name="Esg" required={true} text="Esg Focus" />
                <CheckboxOrRadioGroup
                  name="Esg"
                  type="radio"
                  options={['Yes', 'No', 'Both']}
                  selectedOptions={values.Esg || ''}
                  directionRow={true}
                  controlFunc={handleChange}
                />
                <ErrorMessage
                  name="Esg"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div
                className="form__group form__group--compact"
                style={{ marginBottom: '15px' }}
              >
                <BasicLabel
                  name="Diversity"
                  required={true}
                  text="Diversity Focus"
                />
                <CheckboxOrRadioGroup
                  name="Diversity"
                  type="radio"
                  options={['Yes', 'No', 'Both']}
                  selectedOptions={values.Diversity || ''}
                  directionRow={true}
                  controlFunc={handleChange}
                />
                <ErrorMessage
                  name="Diversity"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div
                className="form__group form__group--compact"
                style={{ marginBottom: '15px' }}
              >
                <BasicLabel
                  name="About"
                  required={false}
                  text="About"
                  styles={{
                    fontWeight: 'bold',
                    marginTop: '15px',
                  }}
                />

                <TextEditor
                  name="About"
                  editorState={values.About}
                  controlFunc={setFieldValue}
                  touchFunc={setFieldTouched}
                  isValid={(!errors.About && !!touched.About) || !touched.About}
                />

                <ErrorMessage
                  name="About"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>
              <div className="form__controls form__controls--compact">
                <BasicButtonGroup groupStyles={{ width: '100%' }}>
                  <BasicSubmitButton
                    controlFunc={handleSubmit}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnArrow={true}
                    btnTextColor="#27282d"
                    btnText="Save"
                  />

                  <BasicButton
                    controlFunc={dismiss}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnArrow={false}
                    btnTextColor="#27282d"
                    btnText="Cancel"
                  />
                </BasicButtonGroup>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </CSSTransition>
  ) : (
    <div className="form__inner">
      <div className="horizontal-loader__container">
        <div className="horizontal-loader__container">
          <HorizontalLoader />
        </div>
      </div>
    </div>
  );
};

export default FirmOverviewEditForm;
